import { Component, EnvironmentInjector, OnInit, Optional, TemplateRef } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { hasPermissionByKey } from "@app/_helpers/permission";
import { useUserRulebreaksParser } from "@app/_helpers/rulebreaks/utils";
import { isComegoLockedForDate, isComegoLockedForDateFuture } from "@app/_helpers/timeLock";
import { ParsedComegoTime } from "@app/_helpers/types";
import {
	createDateRangeArray,
	createRxValue,
	createScheduleDayParser,
	createScheduleDayParserFromUser,
	timeDurationSeconds,
} from "@app/_helpers/utils";
import { CheckWorkingHourErrorsOptions, checkWorkingHourErrors } from "@app/_helpers/workingHourUtils";
import { ClockService } from "@app/_services/clock.service";
import { COMEGO_ICON_MAP } from "@app/components/come-and-go-create-dialog/come-and-go-utils";
import {
	ComeAndGoMultiUpdateDialogComponent,
	WorkingHoursUpdateData,
} from "@app/components/come-and-go-multi-update-dialog/come-and-go-update-dialog.component";
import {
	ComeAndGoUpdateDialogComponent,
	ComeGoUpdateDialogData,
} from "@app/components/come-and-go-update-dialog/come-and-go-update-dialog.component";
import { HomeService } from "@app/pages/home-page/home.service";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ComegoQuery,
	ComegoService,
	ComegoTime,
	ComegoTimeType,
	Logger,
	Time,
	UserSettings,
	UserSettingsQuery,
} from "@timeghost/web-api";
import {
	endOfDay,
	isBefore,
	isSameDay,
	isValid,
	isWithinInterval,
	roundToNearestMinutes,
	startOfDay,
} from "date-fns/esm";
import sorter from "fluent-sorter";
import { combineLatest, filter, map, startWith, switchMap, withLatestFrom } from "rxjs";
import { RecordToolbarService } from "../record-toolbar/record-toolbar.service";
export const disabledFlags: ComegoTimeType[] = ["pause"];
const log = new Logger("TimeComegoViewComponent");
type TimesListView = Array<{
	date: Date;
	id: string;
	times: ParsedComegoTime[];
	schedule: ReturnType<ReturnType<typeof createScheduleDayParser>>;
	editable: boolean;
	deleteable: boolean;
	meta: Record<string, any>;
	sum: { [K in ComegoTimeType]: number } & { realBreak: number };
	allowEdit: boolean;
}>;
@UntilDestroy()
@Component({
	selector: "tg-time-comego-view",
	templateUrl: "./time-comego-view.component.html",
	styleUrls: ["./time-comego-view.component.scss"],
})
export class TimeComegoViewComponent implements OnInit {
	readonly iconMap = COMEGO_ICON_MAP;
	constructor(
		private comegoQuery: ComegoQuery,
		private comego: ComegoService,
		@Optional() private homeService: HomeService,
		private userSettingsQuery: UserSettingsQuery,
		private recordService: RecordToolbarService,
		private dialog: MatDialog,
		private envInjector: EnvironmentInjector,
		private translate: TranslateService,
		private deletionService: DeletionUndoService,
		private clock: ClockService
	) {}
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	readonly range$ = createRxValue<DateRange<Date>>(null);
	private viewRange$ = (this.homeService?.range || this.range$).asObservable(true);
	readonly scheduleEnabled$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => !!x?.workspace?.schedules?.workspace?.enabled));
	readonly times$ = combineLatest([
		this.comegoQuery.selectAll().pipe(
			withLatestFrom(this.userSettingsQuery.select((x) => x.id)),
			map(([x, userId]) => x.filter((t) => t.user.id === userId).sort(sorter((f) => f.start, "desc")))
		),
		this.viewRange$,
		this.clock.now$.pipe(
			untilDestroyed(this),
			withLatestFrom(
				this.comegoQuery.selectAll({ filterBy: (x) => !x.end, limitTo: 1 }),
				this.userSettingsQuery.select()
			),
			filter(([, x, user]) => !!x.find((d) => !d.end && d.user?.id === user.id)),
			map(([now]) => now),
			startWith(new Date())
		), // update combine if a recording is running
	]).pipe(
		untilDestroyed(this),
		map(([times, range, now]) => {
			let newTimes = times.map((t) => {
				const end = t.end ? new Date(t.end) : roundToNearestMinutes(now, { nearestTo: 1 });
				return { ...t, start: new Date(t.start), end: isValid(end) ? end : null, isRecording: !t.end };
			});

			if (range) {
				newTimes = newTimes.filter((t) => isWithinInterval(t.start, range));
			}
			return newTimes.reduce(
				(acc, r) => {
					acc.comego?.push(r as any);
					return acc;
				},
				{ comego: [], length: newTimes.length } as {
					comego: ParsedComegoTime[];
					length: number;
				}
			);
		})
	);
	private checkWorkingHourErrors(times: ComegoTime[], date?: Date, options?: Partial<CheckWorkingHourErrorsOptions>) {
		let errors: ReturnType<typeof checkWorkingHourErrors> = checkWorkingHourErrors.bind(this.envInjector)(
			times,
			this.userSettingsQuery.getValue(),
			date,
			true,
			options
		);
		if (!errors?.length) return null;
		return errors;
	}
	readonly parseRulebreaks = useUserRulebreaksParser();
	readonly times$listView = combineLatest([this.times$, this.viewRange$, this.userSettingsQuery.select()]).pipe(
		switchMap(async ([times, range, userSettings]) => {
			if (!times.comego?.length) return null;
			const user = userSettings.workspace.users.find((x) => x.id === userSettings.id);
			const startPending = startOfDay(new Date());
			const timesDefault = createDateRangeArray(range.start, range.end)[0].map(
				([date]): TimesListView[0] & { isPending: boolean } => ({
					date,
					times: [],
					id: date.toISOString(),
					schedule: null,
					editable: hasPermissionByKey(userSettings, "groupsCanComegoUpdateTimes"),
					deleteable: hasPermissionByKey(userSettings, "groupsCanComegoUpdateTimes"),
					isPending: !isBefore(date, startPending),
					sum: {
						pause: 0,
						realBreak: 0,
						work: 0,
						absence: 0,
					},
					allowEdit: true,
					meta: {},
				})
			) as TimesListView;
			let newTimes: TimesListView = times.comego.reduce(
				(acc, { isRecording, ...r }: ParsedComegoTime & { isRecording: boolean }, i, arr) => {
					if (r.user.id !== userSettings.id) return acc;
					const timeParent = acc.find((x) => isSameDay(x.date, r.start));
					const isValidEnd = r.end && isValid(r.end);
					const timeDiff = timeDurationSeconds(r, r.end ?? new Date());
					if (timeParent) {
						if (timeDiff) timeParent.sum[r.type] += timeDiff;
						if (timeDiff && r.type == "pause") {
							const wh = userSettings.workspace.schedules.workspace["workinghours"] as any;
							const shortBreakDuration = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3600;
							if (timeDiff > shortBreakDuration) timeParent.sum.realBreak += timeDiff;
						}
						timeParent.times.push({
							...r,
							end: isValidEnd ? r.end : null,
							timeDiff,
							meta: {
								paused: r.type === "pause",
								resume: r.type === "work",
								start: i === 0,
								stopped: i === arr.length - 1,
								recording: isRecording,
								single: false,
								type: r.type,
							},
						} as any);
					}
					return acc;
				},
				timesDefault
			);
			if (!newTimes.length) return null;
			newTimes = newTimes.filter((x) => x.times?.length).sort(sorter((x: any) => x.date, "desc"));
			const now = new Date();
			newTimes = (
				await Promise.all(
					newTimes.map(async (t) => {
						t.schedule = createScheduleDayParserFromUser(
							userSettings,
							t.date,
							undefined,
							true
						)?.(t.times.filter((x) => !disabledFlags.includes(x.type) && x.end) as any as Time[], t.date);
						if (t.schedule) {
							const isPastSchedule = startPending.getTime() >= t.date.getTime(),
								pastFailure = isPastSchedule && t.schedule.usage < t.schedule.cap;
							t.schedule = {
								...t.schedule,
								isPastSchedule,
								percent: t.schedule.usage / t.schedule.cap,
								graphState: pastFailure
									? "accent"
									: t.schedule.usage < t.schedule.cap
										? "accent"
										: "success",
								pending: t.schedule.cap - t.schedule.usage,
							} as any;
						}
						const parsed = await this.parseRulebreaks(user, userSettings, t.times as any, {
							from: startOfDay(t.date),
							to: endOfDay(t.date),
						});
						const { times: parsedTimes, issues: workinghoursErrors } = parsed?.[0] ?? ({} as any);
						if (!parsedTimes?.length) return null;
						t.times = parsedTimes.sort(
							sorter((x: ParsedComegoTime) => x.start, "desc").thenBy(
								(x: ParsedComegoTime) => (!x.end ? now : x.end),
								"desc"
							)
						) as any;
						if (workinghoursErrors?.length) t.meta.workinghoursErrors = workinghoursErrors;
						return t;
					})
				)
			).filter(Boolean);
			log.debug("ccview:", newTimes);
			return newTimes;
		})
	);
	ngOnInit(): void {}
	trackId(i: number, { id }: { id: string }) {
		return id;
	}
	editDay(view: TimesListView[0]) {
		return this.dialog
			.open(ComeAndGoMultiUpdateDialogComponent, {
				data: <WorkingHoursUpdateData>{
					time: {
						value: [],
						date: new Date(view.date),
						refetchFromDate: true,
					},
					forceRecreateDay: true,
				},
			})
			.afterClosed()
			.toPromise();
	}
	editItem(refItem: TimesListView[0]["times"][0]) {
		return this.dialog.open(ComeAndGoUpdateDialogComponent, {
			data: {
				entity: refItem as any,
			} as ComeGoUpdateDialogData,
		});
	}

	async deleteItem(
		view: TimesListView[0],
		refItem: TimesListView[0]["times"][0],
		confirmDeleteTemplate?: TemplateRef<any>
	) {
		if (confirmDeleteTemplate) {
			const allowDelete = await this.dialog
				.open(confirmDeleteTemplate, {
					data: {
						entity: refItem,
					},
				})
				.afterClosed()
				.toPromise()
				.then((x) => x === true)
				.catch(() => false);
			if (!allowDelete) {
				return;
			}
		}
		await this.comego.delete(refItem as any);
	}

	async deleteDay(view: TimesListView[0], confirmDeleteTemplate?: TemplateRef<any>) {
		if (confirmDeleteTemplate) {
			const allowDelete = await this.dialog
				.open(confirmDeleteTemplate, {
					data: {
						entity: view.times[0],
					},
				})
				.afterClosed()
				.toPromise()
				.then((x) => x === true)
				.catch(() => false);
			if (!allowDelete) {
				return;
			}
		}
		await this.deletionService.deleteWithUndo({ type: "comegoDay", view, day: view.date });
	}

	isPendingDeletion(viewId: string): boolean {
		return this.deletionService.isPendingDeletion(viewId);
	}

	verifyBlockComegoPast(currentuser: UserSettings, date: Date) {
		return !(isComegoLockedForDate(currentuser, date) || isComegoLockedForDateFuture(currentuser, date));
	}
	isItemEditable(refItem: TimesListView[0]["times"][0]) {
		const userSettings = this.userSettingsQuery.getValue();
		let date = new Date();
		if (!refItem.type) date = new Date(refItem.id);
		else date = new Date(refItem.start);
		return !(isComegoLockedForDate(userSettings, date) || isComegoLockedForDateFuture(userSettings, date));
	}
}
